if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();

        this.quantityInput = this.querySelector('.quantity__input');
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();
        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'))
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;

        this.resetProductFormState();

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;
        const isMobileMediaGalleryChange =
          event.detail?.source === 'media-gallery' && !window.matchMedia('(min-width: 750px)').matches;

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
          targetId: target.id,
          preventFocusScroll: isMobileMediaGalleryChange,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl, {
                prependMedia: !isMobileMediaGalleryChange,
                preserveMediaOrder: isMobileMediaGalleryChange,
              }),
        });
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.toggleSubmitButton(true);
        productForm?.handleErrorMessage();
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, preventFocusScroll = false, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
          })
          .then(() => {
            // set focus to last clicked option value
            document.querySelector(`#${targetId}`)?.focus({ preventScroll: preventFocusScroll });
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            } else {
              console.error(error);
            }
          });
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        return `${url}?${params.join('&')}`;
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
        }
      }

      handleUpdateProductInfo(productUrl, options = {}) {
        return (html) => {
          const variant = this.getSelectedVariant(html);

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);

          if (!variant) {
            this.setUnavailable();
            return;
          }

          this.updateMedia(html, variant?.featured_media?.id, options);
          this.updateSupplementFactsModal(html, variant?.id);

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          updateSourceFromDestination('price');
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Volume');
          updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'));

          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden');

          this.productForm?.toggleSubmitButton(
            html.getElementById(`ProductSubmitButton-${this.sectionId}`)?.hasAttribute('disabled') ?? true,
            window.variantStrings.soldOut
          );

          const sourceSubmitText = html.getElementById(`ProductSubmitButton-${this.sectionId}`)?.querySelector('span');
          const destinationSubmitText = this.querySelector(`#ProductSubmitButton-${this.dataset.section} > span`);
          if (sourceSubmitText && destinationSubmitText) {
            destinationSubmitText.innerHTML = sourceSubmitText.innerHTML;
          }

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });
        };
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          input.value = variantId ?? '';
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      updateURL(url, variantId) {
        const params = new URLSearchParams();
        const sellingPlanId = new URLSearchParams(window.location.search).get('selling_plan');

        if (variantId) params.set('variant', variantId);
        if (sellingPlanId) params.set('selling_plan', sellingPlanId);

        const query = params.toString();
        const updatedUrl = `${url}${query ? `?${query}` : ''}`;

        this.querySelector('share-button')?.updateUrl(`${window.shopUrl}${updatedUrl}`);

        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', updatedUrl);
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(true, window.variantStrings.unavailable);

        const selectors = ['price', 'Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules']
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
        document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));
      }

      updateMedia(html, variantFeaturedMediaId, options = {}) {
        if (!variantFeaturedMediaId) return;

        const mediaGallerySource = this.querySelector('media-gallery ul');
        const mediaGalleryDestination = html.querySelector(`media-gallery ul`);

        const refreshSourceData = () => {
          if (this.hasAttribute('data-zoom-on-hover')) enableZoomOnHover(2);
          const mediaGallerySourceItems = Array.from(mediaGallerySource.querySelectorAll('li[data-media-id]'));
          const sourceSet = new Set(mediaGallerySourceItems.map((item) => item.dataset.mediaId));
          const sourceMap = new Map(
            mediaGallerySourceItems.map((item, index) => [item.dataset.mediaId, { item, index }])
          );
          return [mediaGallerySourceItems, sourceSet, sourceMap];
        };

        if (mediaGallerySource && mediaGalleryDestination) {
          let [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
          const mediaGalleryDestinationItems = Array.from(
            mediaGalleryDestination.querySelectorAll('li[data-media-id]')
          );
          const destinationSet = new Set(mediaGalleryDestinationItems.map(({ dataset }) => dataset.mediaId));
          let shouldRefresh = false;

          // add items from new data not present in DOM
          for (let i = mediaGalleryDestinationItems.length - 1; i >= 0; i--) {
            if (!sourceSet.has(mediaGalleryDestinationItems[i].dataset.mediaId)) {
              mediaGallerySource.prepend(mediaGalleryDestinationItems[i]);
              shouldRefresh = true;
            }
          }

          // remove items from DOM not present in new data
          for (let i = 0; i < mediaGallerySourceItems.length; i++) {
            if (!destinationSet.has(mediaGallerySourceItems[i].dataset.mediaId)) {
              mediaGallerySourceItems[i].remove();
              shouldRefresh = true;
            }
          }

          // refresh
          if (shouldRefresh) [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();

          if (!options.preserveMediaOrder) {
            // if media galleries don't match, sort to match new data order
            mediaGalleryDestinationItems.forEach((destinationItem, destinationIndex) => {
              const sourceData = sourceMap.get(destinationItem.dataset.mediaId);

              if (sourceData && sourceData.index !== destinationIndex) {
                mediaGallerySource.insertBefore(
                  sourceData.item,
                  mediaGallerySource.querySelector(`li:nth-of-type(${destinationIndex + 1})`)
                );

                // refresh source now that it has been modified
                [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
              }
            });
          }
        }

        // set featured media as active in the media gallery
        this.querySelector(`media-gallery`)?.setActiveMedia?.(
          `${this.dataset.section}-${variantFeaturedMediaId}`,
          options.prependMedia ?? true
        );

        // update media modal
        const modalContent = this.productModal?.querySelector(`.product-media-modal__content`);
        const newModalContent = html.querySelector(`product-modal .product-media-modal__content`);
        if (modalContent && newModalContent) modalContent.innerHTML = newModalContent.innerHTML;
      }

      updateSupplementFactsModal(html, variantId = null) {
        const sourceModal = html.getElementById(`SupplementFactsModal-${this.sectionId}`);
        const destinationModal = document.getElementById(`SupplementFactsModal-${this.dataset.section}`);
        if (!sourceModal || !destinationModal) return;

        destinationModal.dataset.hasSupplementFacts = sourceModal.dataset.hasSupplementFacts;
        destinationModal.dataset.currentVariantId = variantId || '';

        const sourceContent = sourceModal.querySelector('[data-supplement-facts-content]');
        const destinationContent = destinationModal.querySelector('[data-supplement-facts-content]');
        if (sourceContent && destinationContent) {
          destinationContent.innerHTML = sourceContent.innerHTML;
        }

        const sourceTemplates = sourceModal.querySelector('[data-supplement-facts-templates]');
        const destinationTemplates = destinationModal.querySelector('[data-supplement-facts-templates]');
        if (sourceTemplates && destinationTemplates) {
          destinationTemplates.innerHTML = sourceTemplates.innerHTML;
        }

        this.renderSupplementFactsForVariant(destinationModal, variantId);
      }

      renderSupplementFactsForVariant(modal, variantId) {
        if (!modal) return;

        const content = modal.querySelector('[data-supplement-facts-content]');
        if (!content || !variantId) return;

        const template = Array.from(modal.querySelectorAll('[data-supplement-facts-template]')).find(
          (element) => element.dataset.variantId === String(variantId)
        );

        if (template) {
          content.innerHTML = template.innerHTML;
          modal.dataset.hasSupplementFacts = 'true';
        } else {
          content.innerHTML = '<p>Supplement facts are not available for this variant.</p>';
          modal.dataset.hasSupplementFacts = 'false';
        }

        modal.dataset.currentVariantId = String(variantId);
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity ? parseInt(this.quantityInput.dataset.cartQuantity) : 0,
          min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1,
          max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max) : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute('max');
        }
        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector('.quantity__rules-cart .loading__spinner').classList.remove('hidden');
        return fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() => this.querySelector('.quantity__rules-cart .loading__spinner').classList.add('hidden'));
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
            if (selector === '.quantity__label') {
              const updatedAriaLabelledBy = updated.getAttribute('aria-labelledby');
              if (updatedAriaLabelledBy) {
                current.setAttribute('aria-labelledby', updatedAriaLabelledBy);
                // Update the referenced visually hidden element
                const labelId = updatedAriaLabelledBy;
                const currentHiddenLabel = document.getElementById(labelId);
                const updatedHiddenLabel = html.getElementById(labelId);
                if (currentHiddenLabel && updatedHiddenLabel) {
                  currentHiddenLabel.textContent = updatedHiddenLabel.textContent;
                }
              }
            }
          }
        }
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'related-products'
        );
        return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`);
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'quick_order_list'
        );
        return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`);
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    }
  );
}

(() => {
  const appstleSelector =
    '[id^="appstle_subscription_widget"], .appstle_sub_widget, .appstle_subscription_wrapper';
  const planTextSelector = [
    '.appstle_one_time_description_text',
    '.appstle_selling_plan_label_1',
    '.appstle_selling_plan_label_2',
    '.appstle_subscription_radio_text',
    '.appstle_frequency_label',
    '.appstle_select option',
  ].join(',');

  const replacementFor = (text) => {
    const normalized = text.replace(/\s+/g, ' ').trim();
    if (!normalized) return null;
    if (/deliver(?:y)? every 30 days/i.test(normalized)) return 'Monthly (most popular)';
    if (/every 30 days/i.test(normalized)) return 'Monthly (most popular)';
    if (/\b1\s*month\b/i.test(normalized)) return 'Monthly (most popular)';
    if (/^monthly$/i.test(normalized)) return 'Monthly (most popular)';
    if (/\b6\s*weeks?\b/i.test(normalized)) return '6 Weeks';
    if (/\b8\s*weeks?\b/i.test(normalized)) return '8 Weeks';
    return null;
  };

  const normalizeAppstlePlanLabels = (root = document) => {
    const hasAppstleWidget =
      root === document || root.matches?.(appstleSelector) || root.querySelector?.(appstleSelector);
    if (!hasAppstleWidget) return;

    const elements = [
      ...(root.matches?.(planTextSelector) ? [root] : []),
      ...root.querySelectorAll(planTextSelector),
    ];

    elements.forEach((element) => {
      const replacement = replacementFor(element.textContent || '');
      if (replacement) element.textContent = replacement;
    });
  };

  document.addEventListener('DOMContentLoaded', () => normalizeAppstlePlanLabels());
  document.addEventListener('appstle_widget_updated', () => normalizeAppstlePlanLabels());

  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (node.nodeType === Node.ELEMENT_NODE) normalizeAppstlePlanLabels(node);
      }
    }
  });

  observer.observe(document.documentElement, { childList: true, subtree: true });
})();

(() => {
  const selector = '[data-ap-purchase-options]';
  const initialized = new WeakSet();

  const getPlanFromUrl = () => new URLSearchParams(window.location.search).get('selling_plan') || '';

  const getVariantId = (form) => form?.querySelector('input[name="id"]')?.value || '';

  const dispatchNativeChange = (input) => {
    if (!input) return;
    input.dispatchEvent(new Event('input', { bubbles: true }));
    input.dispatchEvent(new Event('change', { bubbles: true }));
  };

  const getFormSellingPlanInput = (form) => {
    if (!form) return null;

    let input = form.querySelector('[data-ap-form-selling-plan-input]');
    if (input) return input;

    input = document.createElement('input');
    input.type = 'hidden';
    input.name = 'selling_plan';
    input.dataset.apFormSellingPlanInput = 'true';
    form.appendChild(input);

    return input;
  };

  const updateSellingPlanInputs = (root, form, sellingPlanId) => {
    const hiddenInput = root.querySelector('[data-ap-selling-plan-input]');
    const formHiddenInput = getFormSellingPlanInput(form);

    if (hiddenInput) {
      hiddenInput.value = sellingPlanId;
      hiddenInput.disabled = true;
      dispatchNativeChange(hiddenInput);
    }

    if (formHiddenInput) {
      formHiddenInput.value = sellingPlanId;
      formHiddenInput.disabled = !sellingPlanId;
      dispatchNativeChange(formHiddenInput);
    }
  };

  const planValueSelector = (sellingPlanId) => String(sellingPlanId).replace(/"/g, '\\"');

  const trainingFrequencyText = {
    '6305579318': 'Best for training 5-6x per week',
    '8558936374': 'Best for training 3-4x per week',
    '8558969142': 'Best for training 2-3x per week',
  };

  const updateUrl = (productUrl, variantId, sellingPlanId) => {
    const params = new URLSearchParams(window.location.search);
    if (variantId) params.set('variant', variantId);
    if (sellingPlanId) {
      params.set('selling_plan', sellingPlanId);
    } else {
      params.delete('selling_plan');
    }

    const query = params.toString();
    window.history.replaceState({}, '', `${productUrl}${query ? `?${query}` : ''}`);
  };

  const syncAppstleWidget = (sellingPlanId) => {
    const appstleSelectedPlanInput = document.querySelector('.appstle-selectedSellingPlan');
    if (appstleSelectedPlanInput) {
      appstleSelectedPlanInput.value = sellingPlanId;
      dispatchNativeChange(appstleSelectedPlanInput);
    }

    const purchaseOptionInput = document.querySelector(
      `.appstle_subscription_wrapper input[name="selling_plan"][value="${planValueSelector(sellingPlanId)}"]`
    );
    const oneTimeInput = document.querySelector('.appstle_subscription_wrapper input[name="selling_plan"][value=""]');
    const optionInput = sellingPlanId ? purchaseOptionInput : oneTimeInput;
    if (optionInput && !optionInput.checked) {
      optionInput.click();
    }

    if (sellingPlanId) {
      const sellingPlanRadio = document.querySelector(
        `.appstleRadioSellingPlanWrapper input[name="selling_plan_radio"][value="${planValueSelector(sellingPlanId)}"]`
      );
      if (sellingPlanRadio && !sellingPlanRadio.checked) {
        sellingPlanRadio.click();
      }
    }
  };

  const setSelection = (root, sellingPlanId, options = {}) => {
    const form = document.getElementById(root.dataset.formId);
    const productUrl = root.dataset.productUrl || window.location.pathname;

    root.dataset.selectedSellingPlan = sellingPlanId;
    updateSellingPlanInputs(root, form, sellingPlanId);

    root.querySelectorAll('[data-ap-purchase-choice]').forEach((choice) => {
      const isSelected = choice.dataset.sellingPlan === sellingPlanId;
      choice.classList.toggle('is-selected', isSelected);
      choice.setAttribute('aria-pressed', String(isSelected));
    });

    const subscriptionSelected = Boolean(sellingPlanId);
    root
      .querySelector('[data-ap-subscription-card]')
      ?.classList.toggle('is-selected', subscriptionSelected);
    const trainingFrequency = root.querySelector('[data-ap-training-frequency]');
    if (trainingFrequency) {
      trainingFrequency.textContent =
        trainingFrequencyText[sellingPlanId] || trainingFrequencyText[root.dataset.monthlyPlanId] || '';
    }

    const planSelect = root.querySelector('[data-ap-selling-plan-select]');
    if (planSelect && sellingPlanId) planSelect.value = sellingPlanId;
    const planOption = root.querySelector(`[data-ap-plan-option][data-value="${planValueSelector(sellingPlanId)}"]`);
    const planCurrent = root.querySelector('[data-ap-plan-current]');
    if (planCurrent && planOption) planCurrent.textContent = planOption.textContent.trim();
    root.querySelectorAll('[data-ap-plan-option]').forEach((option) => {
      option.classList.toggle('is-selected', option === planOption);
    });

    if (options.updateUrl) updateUrl(productUrl, getVariantId(form) || root.dataset.variantId, sellingPlanId);
    if (options.syncAppstle !== false) syncAppstleWidget(sellingPlanId);
  };

  const closePlanDropdown = (dropdown) => {
    if (!dropdown) return;
    dropdown.classList.remove('is-open');
    dropdown.querySelector('[data-ap-plan-menu]')?.setAttribute('hidden', '');
    dropdown.querySelector('[data-ap-plan-toggle]')?.setAttribute('aria-expanded', 'false');
  };

  const closeAllPlanDropdowns = (except) => {
    document.querySelectorAll('[data-ap-plan-dropdown]').forEach((dropdown) => {
      if (dropdown !== except) closePlanDropdown(dropdown);
    });
  };

  const initPurchaseOptions = (root) => {
    if (!root || initialized.has(root)) return;
    initialized.add(root);

    root.addEventListener('click', (event) => {
      const planToggle = event.target.closest('[data-ap-plan-toggle]');
      if (planToggle && root.contains(planToggle)) {
        const dropdown = planToggle.closest('[data-ap-plan-dropdown]');
        const menu = dropdown?.querySelector('[data-ap-plan-menu]');
        const shouldOpen = planToggle.getAttribute('aria-expanded') !== 'true';

        closeAllPlanDropdowns(dropdown);
        dropdown?.classList.toggle('is-open', shouldOpen);
        planToggle.setAttribute('aria-expanded', String(shouldOpen));
        if (menu) menu.hidden = !shouldOpen;
        return;
      }

      const planOption = event.target.closest('[data-ap-plan-option]');
      if (planOption && root.contains(planOption)) {
        const dropdown = planOption.closest('[data-ap-plan-dropdown]');
        const select = dropdown?.querySelector('[data-ap-selling-plan-select]');

        if (select) {
          select.value = planOption.dataset.value;
          select.dispatchEvent(new Event('change', { bubbles: true }));
        }
        closePlanDropdown(dropdown);
        return;
      }

      const choice = event.target.closest('[data-ap-purchase-choice]');
      if (!choice || !root.contains(choice)) return;
      const sellingPlanId = choice.dataset.apSubscriptionToggle !== undefined
        ? root.dataset.monthlyPlanId || choice.dataset.sellingPlan || ''
        : choice.dataset.sellingPlan || '';
      setSelection(root, sellingPlanId, { updateUrl: true });
    });

    root.querySelector('[data-ap-selling-plan-select]')?.addEventListener('change', (event) => {
      setSelection(root, event.target.value || root.dataset.monthlyPlanId || '', { updateUrl: true });
    });

    const form = document.getElementById(root.dataset.formId);
    form?.querySelector('input[name="id"]')?.addEventListener('change', () => {
      const currentPlan = form.querySelector('[data-ap-form-selling-plan-input]')?.value || getPlanFromUrl();
      setSelection(root, currentPlan, { updateUrl: true, syncAppstle: false });
    });

    const urlPlan = getPlanFromUrl();
    const initialPlan = urlPlan || root.dataset.monthlyPlanId || '';
    setSelection(root, initialPlan, { updateUrl: Boolean(initialPlan && !urlPlan) });
  };

  const initAllPurchaseOptions = (root = document) => {
    const widgets = [
      ...(root.matches?.(selector) ? [root] : []),
      ...root.querySelectorAll(selector),
    ];
    widgets.forEach(initPurchaseOptions);
  };

  const syncAllPurchaseOptions = () => {
    document.querySelectorAll(selector).forEach((root) => {
      const urlPlan = getPlanFromUrl();
      const currentPlan = Object.prototype.hasOwnProperty.call(root.dataset, 'selectedSellingPlan')
        ? root.dataset.selectedSellingPlan
        : root.dataset.monthlyPlanId || '';

      setSelection(root, urlPlan || currentPlan);
    });
  };

  document.addEventListener('DOMContentLoaded', () => initAllPurchaseOptions());
  document.addEventListener('appstle_widget_updated', () => {
    initAllPurchaseOptions();
    syncAllPurchaseOptions();
  });
  const appstleObserver = new MutationObserver((mutations) => {
    const appstleWidgetAdded = mutations.some((mutation) =>
      Array.from(mutation.addedNodes).some(
        (node) =>
          node.nodeType === Node.ELEMENT_NODE &&
          (node.matches?.('.appstle_subscription_wrapper') || node.querySelector?.('.appstle_subscription_wrapper'))
      )
    );

    if (appstleWidgetAdded) window.setTimeout(syncAllPurchaseOptions, 0);
  });
  appstleObserver.observe(document.documentElement, { childList: true, subtree: true });
  window.addEventListener('popstate', () => {
    syncAllPurchaseOptions();
  });
  document.addEventListener('click', (event) => {
    if (!event.target.closest('[data-ap-plan-dropdown]')) closeAllPlanDropdowns();
  });
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') closeAllPlanDropdowns();
  });
})();

(() => {
  const dropdownSelector = '[data-ap-flavor-dropdown]';

  const closeDropdown = (dropdown) => {
    if (!dropdown) return;
    dropdown.classList.remove('is-open');
    dropdown.querySelector('[data-ap-flavor-menu]')?.setAttribute('hidden', '');
    dropdown.querySelector('[data-ap-flavor-toggle]')?.setAttribute('aria-expanded', 'false');
  };

  const closeAllDropdowns = (except) => {
    document.querySelectorAll(dropdownSelector).forEach((dropdown) => {
      if (dropdown !== except) closeDropdown(dropdown);
    });
  };

  const updateDropdownDisplay = (dropdown, selectedOption) => {
    const select = dropdown?.querySelector('[data-ap-flavor-native]');
    const option =
      selectedOption ||
      Array.from(dropdown?.querySelectorAll('[data-ap-flavor-option]') || []).find(
        (item) => item.dataset.value === select?.value
      );
    const current = dropdown?.querySelector('[data-ap-flavor-current]');

    if (!option || !current) return;
    dropdown.querySelectorAll('[data-ap-flavor-option]').forEach((item) => {
      item.classList.toggle('is-selected', item === option);
    });
    current.innerHTML = option.innerHTML;
  };

  document.addEventListener('click', (event) => {
    const toggle = event.target.closest('[data-ap-flavor-toggle]');
    if (toggle) {
      const dropdown = toggle.closest(dropdownSelector);
      const menu = dropdown?.querySelector('[data-ap-flavor-menu]');
      const shouldOpen = toggle.getAttribute('aria-expanded') !== 'true';

      closeAllDropdowns(dropdown);
      dropdown?.classList.toggle('is-open', shouldOpen);
      toggle.setAttribute('aria-expanded', String(shouldOpen));
      if (menu) menu.hidden = !shouldOpen;
      return;
    }

    const option = event.target.closest('[data-ap-flavor-option]');
    if (option) {
      const dropdown = option.closest(dropdownSelector);
      const select = dropdown?.querySelector('[data-ap-flavor-native]');

      if (select && !option.disabled) {
        select.value = option.dataset.value;
        updateDropdownDisplay(dropdown, option);
        select.dispatchEvent(new Event('change', { bubbles: true }));
      }
      closeDropdown(dropdown);
      return;
    }

    if (!event.target.closest(dropdownSelector)) closeAllDropdowns();
  });

  document.addEventListener('change', (event) => {
    if (!event.target.matches('[data-ap-flavor-native]')) return;
    updateDropdownDisplay(event.target.closest(dropdownSelector));
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') closeAllDropdowns();
  });
})();

(() => {
  const selectDefaultSize = (root = document) => {
    if (new URLSearchParams(window.location.search).has('variant')) return;

    root.querySelectorAll('[data-ap-size-selector]').forEach((container) => {
      const select = container.querySelector('select');
      if (select) {
        const currentOption = select.selectedOptions[0];
        if (currentOption?.textContent?.toLowerCase().includes('25')) return;

        const targetOption = Array.from(select.options).find((option) =>
          option.textContent.toLowerCase().includes('25')
        );

        if (!targetOption || targetOption.disabled) return;
        select.value = targetOption.value;
        select.dispatchEvent(new Event('change', { bubbles: true }));
        return;
      }

      const current = container.querySelector('input[type="radio"]:checked');
      if (current?.value?.toLowerCase().includes('25')) return;

      const target = Array.from(container.querySelectorAll('input[type="radio"]')).find((input) =>
        input.value.toLowerCase().includes('25')
      );

      if (!target || target.disabled || target.classList.contains('disabled')) return;
      target.checked = true;
      target.dispatchEvent(new Event('change', { bubbles: true }));
    });
  };

  document.addEventListener('DOMContentLoaded', () => selectDefaultSize());
  document.addEventListener('product-info:loaded', (event) => selectDefaultSize(event.target));
})();
